import React, { useEffect, useState, useCallback } from 'react';
import {
  Prompt,
  PromptBody,
  MobilePromptBody,
  ModalInteractiveFooter,
  MobileNotOptimizedBanner,
  FlexContainer,
  Button,
} from '@insiderintelligence/componentlibrary';
import { useWindowSize } from '../../helpers/hooks/useWindowSize';
import Track from '../../helpers/Analytics';
import config from '../../config';
import { useParams } from 'react-router-dom';
import {
  DATA_LAYER_TYPE_PAYWALL__NONE,
  DATA_LAYER_TYPE_UID_PREFIX_FORECASTS_PREMIUM,
  DATA_LAYER_TYPE_SECTION__FORECASTS_PREMIUM,
  getAccessTypeOnSubscriptionService,
  getUserSubscriptionTierFromCookies,
  dataLayerRawPush,
} from '@insiderintelligence/datalayer';
import { User } from '@emarketer/auth';
import { ACCESS_TOKEN_COOKIE_NAME, readAccessToken } from '@emarketer/auth';
import Cookies from 'js-cookie';
import { submitToSalesForce, scheduleDemo, createTelemetryLogger } from '@emarketer/code-helpers';

const telemetryLogger = createTelemetryLogger(config.currentEnvironment);

const LockedMetric = () => {
  useEffect(() => {
    Track('Pro-PremiumForecast-LockedAsset', null, true);
  }, []);

  const { isMobile } = useWindowSize();
  const [salesForceSubmitted, setSalesForceSubmitted] = useState(false);
  const [footerButtons, setFooterButtons] = useState([]);

  // dataLayer
  const { metricIds } = useParams();
  useEffect(() => {
    const usrSubTier = getUserSubscriptionTierFromCookies(typeof document !== 'undefined' ? document.cookie : '');
    const sortedKeywords = (metricIds || '').split(',').sort().join(',');
    dataLayerRawPush({
      accessType: getAccessTypeOnSubscriptionService(usrSubTier),
      paywallType: DATA_LAYER_TYPE_PAYWALL__NONE,
      isAdBlock: false,
      isLocked: true,
      keywords: sortedKeywords,
      unifiedAssetId: `${DATA_LAYER_TYPE_UID_PREFIX_FORECASTS_PREMIUM}metric`,
      section: DATA_LAYER_TYPE_SECTION__FORECASTS_PREMIUM,
      userSubscriptionTier: usrSubTier,
    });
  }, [metricIds]);

  const learnMoreOnClickForNonPremium = useCallback(() => {
    Track('Learn-More-Click', {
      hasAccess: false,
    });
    window.open(`${config.baseUrl}/products/forecasts/`, '_blank');
  }, []);

  const learnMoreOnClickForFreemium = useCallback(() => {
    window.open(`${config.baseUrl}/products/pro-plus/`, '_blank');
  }, []);

  const pushPremiumForecastsLeadToSfdcForFreemium = useCallback(() => {
    
    const userAccessToken = readAccessToken(Cookies.get(ACCESS_TOKEN_COOKIE_NAME) ?? '');
    submitToSalesForce(config.salesForceProxyApiUrl, userAccessToken, null, config.sfdcFreemiumProPlusInquiryCampaignId, telemetryLogger);
    scheduleDemo(userAccessToken, config.chilipiperConciergeScriptUrl, config.commonGeolocationUtilityUrl, telemetryLogger);
    setSalesForceSubmitted(true);
  }, []);

  const pushPremiumForecastsLeadToSfdcForNonPremium = useCallback(() => {
    Track('Contact-AccountManager', {
      hasAccess: false,
    });
    const userAccessToken = readAccessToken(Cookies.get(ACCESS_TOKEN_COOKIE_NAME) ?? '');
    submitToSalesForce(config.salesForceProxyApiUrl, userAccessToken, config.sfmcProPlusFormId, null, telemetryLogger);
    setSalesForceSubmitted(true);
  }, []);

  const [modalBody, setModalBody] = useState(null);

  useEffect(() => {
    const hasProUserRole = User.isProUser();

    if (!hasProUserRole) {
      setModalBody(
        isMobile ? (
          <MobilePromptBody.TitleMessageJSXImage
            header="Upgrade to PRO+"
            image={<img width="162" height="162" alt="Locked Logo" src="/img/LockedLogo.svg" />}
            message="PRO+ subscribers get unlimited access to EMARKETER charts and data. Gain an unfair advantage with our exclusive forecast comparisons. Upgrade to gain unmatched insights. "
          />
        ) : (
          <PromptBody.TitleMessageImage
            flexToImage
            header="Upgrade to PRO+"
            image={<img width="162" height="162" alt="Locked Logo" src="/img/LockedLogo.svg" />}
            message="PRO+ subscribers get unlimited access to EMARKETER charts and data. Gain an unfair advantage with our exclusive forecast comparisons. Upgrade to gain unmatched insights. "
            flex
          />
        )
      );

      setFooterButtons([
        <ModalInteractiveFooter
          isMobile={isMobile}
          leftButtonCallback={() => pushPremiumForecastsLeadToSfdcForFreemium()}
          rightButtonCallback={() => learnMoreOnClickForFreemium()}
          bottomText="If you need immediate assistance, email us at sales@emarketer.com"
          leftButtonText="Schedule a Demo"
          rightButtonText="Learn More"
          timeoutDuration={5}
          leftButtonAnalytics="PremiumForecasts-ScheduleADemo"
          rightButtonAnalytics="PremiumForecasts-LearnMore"
          confirmationMessageHeading="Request successfully sent"
          confirmationMessageDescription="Thank you. A member of our team will reach out shortly."
        />,
      ]);
    } else {
      // Update modal body content based on salesForceSubmitted state
      setModalBody(
        isMobile ? (
          !salesForceSubmitted ? (
            <MobilePromptBody.TitleMessageJSXImage
              header="Premium Forecast Comparisons"
              image={<img width="162" height="162" alt="Locked Logo" src="/img/LockedLogo.svg" />}
              message="Gain an unfair advantage with our exclusive forecast comparisons. Upgrade to gain unmatched insights."
            />
          ) : (
            <MobilePromptBody.TitleMessageJSXImage
              header="Thank you!"
              message="Thank you for your interest in Compare Forecasts. Your Account Manager will be in touch to help get you access."
              image={<img width="162" height="162" alt="Locked Logo" src="/img/Success_Mark.svg" />}
            />
          )
        ) : salesForceSubmitted ? (
          <PromptBody.TitleMessageJSXImage
            header="Thank you!"
            message="Thank you for your interest in Compare Forecasts. Your Account Manager will be in touch to help get you access."
            isMobile={isMobile}
            image={<img width="162" height="162" alt="Locked Logo" src="/img/Success_Mark.svg" />}
          />
        ) : (
          <PromptBody.TitleMessageImage
            flexToImage
            header="Premium Forecast Comparisons"
            image={<img width="162" height="162" alt="Locked Logo" src="/img/LockedLogo.svg" />}
            message="Gain an unfair advantage with our exclusive forecast comparisons. Upgrade to gain unmatched insights."
            flex
          />
        )
      );
      const modalButtons = [];
      modalButtons.push(
        <Button callback={() => learnMoreOnClickForNonPremium()} buttonType="white" size="m">
          Learn More
        </Button>
      );
      modalButtons.push(
        <>
          <Button callback={() => pushPremiumForecastsLeadToSfdcForNonPremium()} buttonType="default" size="m">
            Contact your Account Manager
          </Button>
        </>
      );
      setFooterButtons(salesForceSubmitted === false && modalButtons);
    }
  }, [
    salesForceSubmitted,
    isMobile,
    pushPremiumForecastsLeadToSfdcForNonPremium,
    pushPremiumForecastsLeadToSfdcForFreemium,
    learnMoreOnClickForNonPremium,
    learnMoreOnClickForFreemium,
  ]);

  return (
    <FlexContainer style={{ overflow: 'hidden' }}>
      <img
        style={{ width: '100%', maxHeight: '1000px', objectFit: 'contain', maxWidth: '1230px' }}
        src={`/img/${isMobile ? 'Compare-Mobile-Image.png' : 'Compare-Locked-Image-Desktop.png'}`}
        alt="Compare Metrics Locked"
      />
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          position: 'absolute',
          paddingTop: '24px',
          left: 0,
          top: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: 'calc(100% + 48px)',
            height: 'calc(100% + 48px)',
            top: '-24px',
            left: '-24px',
          }}
        >
          <div
            style={{
              position: 'absolute',
              zIndex: 2,
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.5)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              width: isMobile ? 'calc(100% - 96px)' : '592px',
              top: isMobile ? '40px' : '160px',
              left: 0,
              right: 0,
              margin: '0 auto',
              zIndex: 3,
              backgroundColor: 'transparent',
            }}
          >
            {isMobile && (
              <div style={{ marginBottom: '16px' }}>
                <MobileNotOptimizedBanner detail="For the best experience, please log in on desktop and navigate to Forecasts." />
              </div>
            )}
            <div style={{ background: 'white', borderRadius: '4px', overflow: 'hidden' }}>
              <Prompt isMobile={isMobile} children={modalBody} footerButtons={footerButtons} />
            </div>
          </div>
        </div>
      </div>
    </FlexContainer>
  );
};

export default LockedMetric;
